import React from 'react';
import moment from 'moment-timezone';
import {
  DevicePhoneMobileIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import {
  OrderStatusBadge,
  PaymentStatusBadge,
  PlatformBadge,
  StatusBadge,
} from '../../components/ui';
import { formatCurrency, formatPhoneNumber } from '../../utils/baseUtils';
import { headerCls } from '../../data/columns';
import { GOOGLE_ADS_SOURCE } from '../integrations/google-ads/google-ads-settings';

const orderTime = (order) => {
  const tz = order?.store?.timezoneid;
  return tz
    ? moment(order.createdate).tz(tz)
    : moment(order.createdate).utcOffset(order.timezone * 60);
};

export const buildOrderColumns = ({ isAdmin }) =>
  [
    {
      title: <span className={headerCls}>Receipt#</span>,
      key: 'receipt',
      render: (_, order) => (
        <div className="flex items-center gap-1.5 text-sm font-bold text-slate-900 dark:text-slate-100">
          {order.ordernumberstr}
          {order.hasrefundapproval ? (
            <ExclamationTriangleIcon
              className="h-4 w-4 flex-shrink-0 text-rose-500"
              title="Refund approval pending"
            />
          ) : null}
        </div>
      ),
    },
    isAdmin && {
      title: <span className={headerCls}>Company</span>,
      key: 'company',
      render: (_, order) => (
        <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
          {order.store?.company?.name}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Customer</span>,
      key: 'customer',
      render: (_, order) => (
        <div>
          <div className="flex items-center gap-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {order.name || order.customer?.fullname || '-'}
            {order.customer?.ismobile ? (
              <DevicePhoneMobileIcon className="h-4 w-4 text-slate-400" />
            ) : null}
          </div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {formatPhoneNumber(order?.phone || order.customer?.phone)}
          </p>
        </div>
      ),
    },
    {
      title: <span className={headerCls}>Details</span>,
      key: 'details',
      render: (_, order) => (
        <div>
          <div className="flex items-center gap-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {order.ordertype === 10 ? 'Dine-In' : 'To Go'}
            {order.pickuptime ? (
              <CalendarDaysIcon className="h-4 w-4 flex-shrink-0 text-amber-500" />
            ) : null}
          </div>
          <p className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {order.store?.name}
          </p>
        </div>
      ),
    },
    {
      title: <span className={headerCls}>Payment Status</span>,
      key: 'payment',
      render: (_, order) => <PaymentStatusBadge status={order.paymentstatus} />,
    },
    {
      title: <span className={headerCls}>Time</span>,
      key: 'time',
      render: (_, order) => {
        const when = orderTime(order);
        return (
          <div>
            <p className="text-sm font-medium tabular-nums text-slate-900 dark:text-slate-100">
              {when.format('hh:mm A')}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {when.format('MMM D')}
            </p>
          </div>
        );
      },
    },
    {
      title: <span className={headerCls}>Order Type</span>,
      key: 'platform',
      render: (_, order) => (
        <div className="flex flex-col items-start gap-1">
          <PlatformBadge status={order.platform} />
          {order.marketingsource === GOOGLE_ADS_SOURCE ? (
            <StatusBadge tone="sky" size="sm" label="Google Ads" />
          ) : null}
        </div>
      ),
    },
    {
      title: <span className={headerCls}>Total</span>,
      key: 'total',
      render: (_, order) => (
        <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100">
          {formatCurrency(order.total - order.refund)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Order Status</span>,
      key: 'status',
      render: (_, order) => (
        <OrderStatusBadge
          status={order.orderstatus}
          label={
            order.orderstatus === 40 && order.refund !== order.total
              ? 'Partial Refund'
              : undefined
          }
        />
      ),
    },
  ].filter(Boolean);
